const KEYBOARD_THRESHOLD = 120;

const scheduleAnimationFrame = (callback) => {
  if (typeof requestAnimationFrame === "function") return requestAnimationFrame(callback);
  return setTimeout(callback, 0);
};

const cancelScheduledAnimationFrame = (frame) => {
  if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(frame);
  else clearTimeout(frame);
};

export const readPlayerRequestViewport = (visualViewport, layoutHeight) => {
  const offsetTop = Math.max(0, Math.round(visualViewport?.offsetTop || 0));
  const viewportHeight = Math.max(1, Math.round(visualViewport?.height || layoutHeight || 1));
  const bottomInset = Math.max(0, Math.round((layoutHeight || viewportHeight) - viewportHeight - offsetTop));
  const keyboardOpen = Boolean(visualViewport && bottomInset >= KEYBOARD_THRESHOLD);
  return {
    viewportHeight,
    offsetTop,
    // Browser chrome / a residual iOS viewport inset is not a keyboard spacer.
    keyboardInset: keyboardOpen ? bottomInset : 0,
    keyboardOpen,
  };
};

export const watchPlayerRequestViewport = ({
  visualViewport,
  fallbackTarget,
  getLayoutHeight,
  onChange,
  scheduleFrame = scheduleAnimationFrame,
  cancelFrame = cancelScheduledAnimationFrame,
}) => {
  let frame = null;
  const update = () => {
    frame = null;
    onChange(readPlayerRequestViewport(visualViewport, getLayoutHeight()));
  };
  const scheduleUpdate = () => {
    if (frame !== null) return;
    frame = scheduleFrame(update);
  };
  const resizeTarget = visualViewport || fallbackTarget;
  update();
  if (!resizeTarget) return () => {};
  resizeTarget.addEventListener("resize", scheduleUpdate);
  // iOS can finish keyboard dismissal/panning with a scroll event after its
  // last resize. Recompute the visible bottom, without scrolling any content.
  visualViewport?.addEventListener("scroll", scheduleUpdate);
  if (visualViewport && fallbackTarget) fallbackTarget.addEventListener("resize", scheduleUpdate);
  return () => {
    resizeTarget.removeEventListener("resize", scheduleUpdate);
    visualViewport?.removeEventListener("scroll", scheduleUpdate);
    if (visualViewport && fallbackTarget) fallbackTarget.removeEventListener("resize", scheduleUpdate);
    if (frame !== null) cancelFrame(frame);
  };
};

export const lockPlayerRequestDocument = ({ documentElement, body, root }) => {
  const targets = [documentElement, body, root].filter(Boolean);
  const added = targets.filter((target) => !target.classList.contains("player-request-thread-open"));
  added.forEach((target) => target.classList.add("player-request-thread-open"));
  return () => added.forEach((target) => target.classList.remove("player-request-thread-open"));
};

export const focusPlayerRequestComposerWithoutScroll = (element, activeElement) => {
  if (!element || activeElement === element || typeof element.focus !== "function") return false;
  try {
    element.focus({ preventScroll: true });
    return true;
  } catch (_) {
    return false;
  }
};
